import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ChevronLeft, Calendar, BookOpen, Trophy, Camera, Sparkles,
  Users, Star, Gift, Heart, Flag, Briefcase, GraduationCap,
  TrendingUp, Award, Mountain, Baby, Book, Utensils, Clock, Download, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = '';

const TIMELINE_BANNER = 'https://static.prod-images.emergentagent.com/jobs/6e8c71c9-5deb-44de-959d-34c14877fd56/images/f7e0df8d419249bf41d8da08a164b122259aa068fb46eb9d6996076a1d2b6f85.jpeg';

// Icon mapping
const ICON_MAP = {
  'calendar': Calendar,
  'book-open': BookOpen,
  'book': Book,
  'trophy': Trophy,
  'camera': Camera,
  'sparkles': Sparkles,
  'users': Users,
  'star': Star,
  'gift': Gift,
  'heart': Heart,
  'flag': Flag,
  'briefcase': Briefcase,
  'graduation-cap': GraduationCap,
  'trending-up': TrendingUp,
  'award': Award,
  'mountain': Mountain,
  'baby': Baby,
  'utensils': Utensils
};

// Color mapping for timeline items
const COLOR_MAP = {
  'blue': 'bg-blue-500 border-blue-400',
  'amber': 'bg-amber-500 border-amber-400',
  'green': 'bg-green-500 border-green-400',
  'purple': 'bg-purple-500 border-purple-400',
  'yellow': 'bg-yellow-500 border-yellow-400',
  'red': 'bg-red-500 border-red-400'
};

const TEXT_COLOR_MAP = {
  'blue': 'text-blue-400',
  'amber': 'text-amber-400',
  'green': 'text-green-400',
  'purple': 'text-purple-400',
  'yellow': 'text-yellow-400',
  'red': 'text-red-400'
};

const BG_COLOR_MAP = {
  'blue': 'bg-blue-500/10 hover:bg-blue-500/20',
  'amber': 'bg-amber-500/10 hover:bg-amber-500/20',
  'green': 'bg-green-500/10 hover:bg-green-500/20',
  'purple': 'bg-purple-500/10 hover:bg-purple-500/20',
  'yellow': 'bg-yellow-500/10 hover:bg-yellow-500/20',
  'red': 'bg-red-500/10 hover:bg-red-500/20'
};

export default function FamilyTimeline() {
  const { familyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('session_token');

  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  
  // Add story/milestone form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormType, setAddFormType] = useState('story'); // 'story' or 'milestone'
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'other',
    featured_family_member: '',
    time_period: '',
    date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTimeline();
  }, [familyId, token]);

  const fetchTimeline = async () => {
    try {
      const [timelineRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/families/${familyId}/timeline`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/families/${familyId}/timeline/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTimelineData(timelineRes.data);
      setStats(statsRes.data);
      setFamily({ name: timelineRes.data.family_name });
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToTimeline = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (addFormType === 'story') {
        await axios.post(`${API}/api/stories`, {
          family_id: familyId,
          title: formData.title,
          content: formData.content,
          category: formData.category,
          featured_family_member: formData.featured_family_member,
          time_period: formData.time_period,
          tags: []
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Story added to timeline!');
      } else {
        // Add as career goal/milestone
        await axios.post(`${API}/api/career-goals`, {
          family_id: familyId,
          title: formData.title,
          description: formData.content,
          target_date: formData.date || null
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Milestone added to timeline!');
      }
      
      setShowAddForm(false);
      setFormData({ title: '', content: '', category: 'other', featured_family_member: '', time_period: '', date: '' });
      fetchTimeline();
    } catch (error) {
      console.error('Failed to add to timeline:', error);
      toast.error('Failed to add to timeline');
    } finally {
      setSubmitting(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Flag;
    return IconComponent;
  };

  const filteredItems = () => {
    if (!timelineData?.items) return [];
    
    let items = timelineData.items;
    
    if (selectedYear !== 'all') {
      items = items.filter(item => {
        const itemYear = item.date ? item.date.substring(0, 4) : null;
        return itemYear === selectedYear;
      });
    }
    
    if (selectedType !== 'all') {
      items = items.filter(item => item.type === selectedType);
    }
    
    return items;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'event': 'Event',
      'story': 'Story',
      'milestone': 'Milestone',
      'photo': 'Photo',
      'prayer_answered': 'Answered Prayer'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  const items = filteredItems();

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(`/family/${familyId}`)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Family
            </Button>
            <div className="ml-auto flex gap-2">
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-to-timeline-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add to Timeline
              </Button>
              <Button 
                onClick={() => navigate(`/family/${familyId}/timeline/export`)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="export-timeline-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export / Print
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">
                {family?.name} Legacy Timeline
              </h1>
              <p className="text-slate-400">
                A journey through your family's memories and milestones
              </p>
            </div>
            {stats && (
              <div className="hidden md:flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{stats.total_items}</p>
                  <p className="text-xs text-slate-400">Total Moments</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-400">{stats.by_type?.events || 0}</p>
                  <p className="text-xs text-slate-400">Events</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{stats.by_type?.stories || 0}</p>
                  <p className="text-xs text-slate-400">Stories</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Banner */}
      <div className="max-w-5xl mx-auto px-4 pt-6" data-testid="timeline-banner">
        <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-lg">
          <img
            src={TIMELINE_BANNER}
            alt="A multi-generational family looking through photo albums and photos on a modern outdoor patio"
            className="w-full h-auto"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Year:</span>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="bg-slate-800 border-slate-600 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {timelineData?.available_years?.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Type:</span>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="bg-slate-800 border-slate-600 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="event">Events</SelectItem>
                <SelectItem value="story">Stories</SelectItem>
                <SelectItem value="milestone">Milestones</SelectItem>
                <SelectItem value="photo">Photos</SelectItem>
                <SelectItem value="prayer_answered">Answered Prayers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto text-sm text-slate-400">
            Showing {items.length} items
          </div>
        </div>

        {/* Timeline */}
        {items.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-12 text-center">
              <Clock className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Timeline Items Yet</h3>
              <p className="text-slate-400 mb-4">
                Start building your family's legacy by creating events, sharing stories, and celebrating milestones.
              </p>
              <div className="flex justify-center gap-2">
                <Button 
                  onClick={() => navigate(`/family/${familyId}`)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Create Event
                </Button>
                <Button 
                  onClick={() => navigate(`/family/${familyId}/spiritual`)}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Share Story
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500 via-blue-500 to-purple-500 opacity-30"></div>
            
            <div className="space-y-6">
              {items.map((item, index) => {
                const IconComponent = getIcon(item.icon);
                const colorClass = COLOR_MAP[item.color] || COLOR_MAP.blue;
                const textColor = TEXT_COLOR_MAP[item.color] || TEXT_COLOR_MAP.blue;
                const bgColor = BG_COLOR_MAP[item.color] || BG_COLOR_MAP.blue;
                
                return (
                  <div key={`${item.type}-${item.id}`} className="relative flex gap-4 group">
                    {/* Timeline dot */}
                    <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center ${colorClass} border-2 shadow-lg group-hover:scale-110 transition-transform`}>
                      <IconComponent className="h-5 w-5 text-white" />
                    </div>
                    
                    {/* Content card */}
                    <Card className={`flex-1 bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer ${bgColor}`}
                      onClick={() => {
                        if (item.type === 'event') {
                          navigate(`/event/${item.id}`);
                        } else if (item.type === 'story') {
                          navigate(`/family/${familyId}/spiritual?tab=stories`);
                        } else if (item.type === 'milestone') {
                          navigate(`/family/${familyId}/spiritual?tab=career`);
                        } else if (item.type === 'photo') {
                          navigate(`/family/${familyId}/gallery`);
                        } else if (item.type === 'prayer_answered') {
                          navigate(`/family/${familyId}/spiritual?tab=prayers`);
                        }
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-white group-hover:text-amber-300 transition-colors">
                              {item.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className={`text-xs ${textColor} border-current`}>
                                {getTypeLabel(item.type)}
                              </Badge>
                              {item.metadata?.category && (
                                <Badge variant="secondary" className="text-xs bg-slate-700">
                                  {item.metadata.category}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-medium ${textColor}`}>
                              {item.date_display}
                            </p>
                            {item.author_name && (
                              <p className="text-xs text-slate-500">
                                by {item.author_name}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {item.description && (
                          <p className="text-slate-400 text-sm mt-2 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                        
                        {/* Metadata */}
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          {item.location && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              📍 {item.location}
                            </span>
                          )}
                          {item.metadata?.featured_family_member && (
                            <span className="text-xs text-amber-400">
                              About: {item.metadata.featured_family_member}
                            </span>
                          )}
                          {item.metadata?.time_period && (
                            <span className="text-xs text-slate-500">
                              ({item.metadata.time_period})
                            </span>
                          )}
                          {item.metadata?.likes > 0 && (
                            <span className="text-xs text-pink-400 flex items-center gap-1">
                              <Heart className="h-3 w-3" /> {item.metadata.likes}
                            </span>
                          )}
                          {item.metadata?.prayer_count > 0 && (
                            <span className="text-xs text-purple-400 flex items-center gap-1">
                              <Sparkles className="h-3 w-3" /> {item.metadata.prayer_count} prayed
                            </span>
                          )}
                          {item.metadata?.tags?.length > 0 && (
                            <div className="flex gap-1">
                              {item.metadata.tags.slice(0, 3).map((tag, i) => (
                                <Badge key={i} variant="outline" className="text-xs text-slate-400 border-slate-600">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Photo thumbnail */}
                        {item.type === 'photo' && item.metadata?.file_url && (
                          <div className="mt-3">
                            <img 
                              src={item.metadata.file_url} 
                              alt={item.title}
                              className="w-full h-32 object-cover rounded-md opacity-80 group-hover:opacity-100 transition-opacity"
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add to Timeline Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center px-4 z-50" onClick={(e) => e.target === e.currentTarget && setShowAddForm(false)}>
          <Card className="bg-slate-800 border-slate-700 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Add to Timeline</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAddForm(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Type selector */}
              <div className="flex gap-2 mb-6">
                <Button
                  type="button"
                  onClick={() => setAddFormType('story')}
                  className={`flex-1 ${addFormType === 'story' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-700 hover:bg-slate-600'}`}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Story
                </Button>
                <Button
                  type="button"
                  onClick={() => setAddFormType('milestone')}
                  className={`flex-1 ${addFormType === 'milestone' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-700 hover:bg-slate-600'}`}
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Milestone
                </Button>
              </div>

              <form onSubmit={handleAddToTimeline} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    autoComplete="off"
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none"
                    placeholder={addFormType === 'story' ? "e.g., Grandma's Secret Recipe" : "e.g., First Family Reunion"}
                    data-testid="timeline-title-input"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 block mb-1">
                    {addFormType === 'story' ? 'Story Content *' : 'Description *'}
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    required
                    rows={5}
                    autoComplete="off"
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none resize-y"
                    placeholder={addFormType === 'story' ? "Share your family story..." : "Describe this milestone..."}
                    data-testid="timeline-content-input"
                  />
                </div>

                {addFormType === 'story' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-slate-400 block mb-1">Featured Family Member</label>
                        <input
                          type="text"
                          value={formData.featured_family_member}
                          onChange={(e) => setFormData({...formData, featured_family_member: e.target.value})}
                          autoComplete="off"
                          className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none"
                          placeholder="e.g., Grandpa Joe"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-slate-400 block mb-1">Time Period</label>
                        <input
                          type="text"
                          value={formData.time_period}
                          onChange={(e) => setFormData({...formData, time_period: e.target.value})}
                          autoComplete="off"
                          className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none"
                          placeholder="e.g., 1960s"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-400 block mb-1">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none"
                      >
                        <option value="childhood">Childhood Memories</option>
                        <option value="traditions">Family Traditions</option>
                        <option value="achievements">Achievements</option>
                        <option value="challenges">Challenges Overcome</option>
                        <option value="values">Values & Lessons</option>
                        <option value="wisdom">Words of Wisdom</option>
                        <option value="recipes">Family Recipes</option>
                        <option value="other">Other Stories</option>
                      </select>
                    </div>
                  </>
                )}

                {addFormType === 'milestone' && (
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Date (optional)</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white text-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      style={{ fontSize: '18px', minHeight: '50px' }}
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    data-testid="submit-timeline-btn"
                  >
                    {submitting ? 'Adding...' : `Add ${addFormType === 'story' ? 'Story' : 'Milestone'}`}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Floating Quick Add Button */}
      {!showAddForm && items.length > 3 && (
        <button
          onClick={() => setShowAddForm(true)}
          className="fixed bottom-8 right-8 w-14 h-14 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center z-40"
          title="Add to Timeline"
          data-testid="floating-add-btn"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
